/**
 * Subscription state helpers.
 *
 * Source of truth is the backend (`/api/subscription/status/:sessionId`), but
 * once a checkout completes we mirror the resulting status into localStorage
 * so the UI can decide instantly whether a visitor is a paid subscriber,
 * still inside their 7-day free preview, or preview-expired. Anything that
 * must be *authoritative* (report exports, live Tesla sync, saved history)
 * is still enforced on the backend.
 *
 * Tiers used across the app:
 *   "preview"  — free 7-day window (unlimited access while it lasts)
 *   "basic"    — $9.99/mo
 *   "yearly"   — $99.99/yr          ← where the differentiators land
 *   "lifetime" — $199.99 one-time
 *   null       — expired preview, no plan
 */

const STATUS_KEY = "eviq.subscription_status";      // "active" | "trialing" | "canceled" | null
const TIER_KEY = "eviq.subscription_tier";          // "basic" | "yearly" | "lifetime"
const PREVIEW_STARTED_KEY = "eviq.preview_started_at"; // epoch ms of first /app visit
const OWNER_MODE_KEY = "eviq.owner_mode";           // "1" if the account owner is previewing
const FOUNDING_MEMBER_KEY = "eviq.founding_member"; // JSON: {email, badge, name, activated_at}
const OPEN_GATE_EVENT = "eviq:open-welcome-gate";

/**
 * Founding-member allowlist.
 *
 * Founding members are hand-picked early supporters who get lifetime-tier
 * access without paying. Everything premium unlocks, admin does NOT.
 * They get a personal activation URL (`/app?founding=<code>`) and a
 * numbered badge. Once activated the flag persists in localStorage.
 *
 * Adding a new founding member: append one entry below with a unique
 * `code` (used in the activation URL), the member's email, and their
 * badge number (e.g. "000003"). No backend deploy needed.
 */
export const FOUNDING_MEMBERS = [
  {
    code: "EVIQ_FOUNDING_PRESTON_000002",
    email: "preston320@yahoo.com",
    name: "Preston",
    badge: "#000002",
  },
  {
    code: "EVIQ_FOUNDING_RANDY_000003",
    email: "randy.hoover@gmail.com",
    name: "Randy",
    badge: "#000003",
  },
];

const DAY_MS = 24 * 60 * 60 * 1000;
const PREVIEW_DAYS = 7;

/** Read the current subscription status from localStorage. */
export function getSubscriptionStatus() {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(STATUS_KEY);
}

/** Read the current subscription tier, if any. */
export function getSubscriptionTier() {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(TIER_KEY);
}

/** Legacy alias used by an older subscription check — kept for compat.
    Owner mode + founding members unlock it too so any caller using the
    old API is covered. */
export function isSubscribed() {
  if (isOwnerMode() || isFoundingMember()) return true;
  const status = getSubscriptionStatus();
  return status === "active" || status === "trialing";
}

/**
 * Return the current effective tier: paid tier if any, else "preview" if
 * still inside the 7-day window, else null.
 *
 * Owner-mode override: if the account owner has enabled owner mode from the
 * admin dashboard, treat them as "yearly" so every premium view unlocks.
 * Founding-member override: treated as "lifetime" (same as a paid lifetime).
 */
export function getEffectiveTier() {
  if (isOwnerMode()) return "yearly";
  if (isFoundingMember()) return "lifetime";
  const paid = getSubscriptionTier();
  if (paid === "basic" || paid === "yearly" || paid === "lifetime") return paid;
  const preview = getPreviewState();
  if (preview.active) return "preview";
  return null;
}

/**
 * Return true for tiers that unlock EVIQ's premium differentiators
 * (Battery Life Predictor, AI narrative, warranty PDF, cell health map,
 * marketplace value). Basic and expired-preview return false.
 * Owner mode + founding members also unlock everything.
 */
export function hasPremiumFeatures() {
  if (isOwnerMode() || isFoundingMember()) return true;
  const tier = getEffectiveTier();
  return tier === "yearly" || tier === "lifetime" || tier === "preview";
}

/** Seller tools — Recall Compliance Report and other buyer-facing documents.
    Genuinely paid: yearly & lifetime only (the 7-day preview and Basic do
    NOT unlock these). Owner mode (yearly) and founding members (lifetime)
    are covered by getEffectiveTier's overrides. */
export function hasSellerTools() {
  const tier = getEffectiveTier();
  return tier === "yearly" || tier === "lifetime";
}

/** Any paid tier — the visitor has committed money. Founding members count
    as paid so the preview-expired hard-lock never shows for them. */
export function hasPaidSubscription() {
  if (isFoundingMember()) return true;
  const tier = getSubscriptionTier();
  return tier === "basic" || tier === "yearly" || tier === "lifetime";
}

/**
 * Return the current preview state:
 *   { started: number|null, daysUsed, daysRemaining, active, expired }
 * Starts the preview timer the first time the visitor lands on /app.
 */
export function getPreviewState() {
  // Owner accounts opt out of the preview entirely — never started, never
  // expiring. This keeps countdown banners and the hard-lock overlay from
  // ever rendering for the account owner regardless of how the /app is
  // navigated to (fresh device, incognito, cleared cache).
  if (typeof window !== "undefined" && isOwnerMode()) {
    return { started: null, daysUsed: 0, daysRemaining: PREVIEW_DAYS, active: false, expired: false };
  }
  if (typeof window === "undefined") {
    return { started: null, daysUsed: 0, daysRemaining: PREVIEW_DAYS, active: true, expired: false };
  }
  const raw = window.localStorage.getItem(PREVIEW_STARTED_KEY);
  const started = raw ? parseInt(raw, 10) : null;
  if (!started) {
    return { started: null, daysUsed: 0, daysRemaining: PREVIEW_DAYS, active: true, expired: false };
  }
  const daysUsed = Math.max(0, (Date.now() - started) / DAY_MS);
  const daysRemaining = Math.max(0, PREVIEW_DAYS - daysUsed);
  return {
    started,
    daysUsed: +daysUsed.toFixed(2),
    daysRemaining: +daysRemaining.toFixed(2),
    active: daysRemaining > 0,
    expired: daysRemaining <= 0,
  };
}

/** Start (or no-op if already started) the 7-day free-preview timer.
    Also skipped entirely when in owner mode — the owner account never
    enters the preview funnel. */
export function ensurePreviewStarted() {
  if (typeof window === "undefined") return;
  if (isOwnerMode()) return;
  if (window.localStorage.getItem(PREVIEW_STARTED_KEY)) return;
  window.localStorage.setItem(PREVIEW_STARTED_KEY, String(Date.now()));
  window.dispatchEvent(new CustomEvent("eviq:subscription-changed"));
}

/** Mark the current visitor as subscribed (call this after Stripe success). */
export function markSubscribed(status = "trialing", tier = "yearly") {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STATUS_KEY, status);
  window.localStorage.setItem(TIER_KEY, tier);
  window.dispatchEvent(new CustomEvent("eviq:subscription-changed"));
}

/** Clear subscription state (for testing / logout). */
export function clearSubscription() {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(STATUS_KEY);
  window.localStorage.removeItem(TIER_KEY);
  window.dispatchEvent(new CustomEvent("eviq:subscription-changed"));
}

/** For QA & tests — reset the preview countdown so it starts over. */
export function resetPreviewCountdown() {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(PREVIEW_STARTED_KEY);
  window.dispatchEvent(new CustomEvent("eviq:subscription-changed"));
}

/**
 * Owner-mode helpers.
 *
 * The account owner (whoever holds the admin credentials) needs to be able
 * to test the full paid mobile experience without buying a subscription.
 * From /admin they can trigger `setOwnerMode(true)` which flips a
 * localStorage flag; `getEffectiveTier()` and `hasPremiumFeatures()`
 * then treat the visitor as a "yearly" subscriber.
 *
 * This flag never affects real subscribers and can only be reached by
 * someone with admin credentials via the admin dashboard, so it's safe
 * even though it lives on the client.
 */
export function isOwnerMode() {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(OWNER_MODE_KEY) === "1";
  } catch (_e) {
    return false;
  }
}

export function setOwnerMode(on) {
  if (typeof window === "undefined") return;
  if (on) {
    window.localStorage.setItem(OWNER_MODE_KEY, "1");
    // Owner accounts don't participate in the 7-day free preview — clear
    // the timer so no "preview ending" banners or hard-locks can ever
    // fire in this browser after ownership is established. Toggling
    // owner-mode OFF later will start a fresh preview from that moment.
    window.localStorage.removeItem(PREVIEW_STARTED_KEY);
  } else {
    window.localStorage.removeItem(OWNER_MODE_KEY);
  }
  window.dispatchEvent(new CustomEvent("eviq:subscription-changed"));
}

/**
 * Founding-member helpers.
 *
 * A founding-member entry looks like `{ email, name, badge, activated_at }`
 * and is stored as JSON under `eviq.founding_member`. When present, the
 * visitor is treated as a lifetime subscriber (`hasPremium=true`,
 * `paid=true`) but admin routes remain independently gated on the backend.
 *
 * Activation is via a per-member URL (`/app?founding=<code>`) — see
 * MobileApp.jsx for the URL handler. The code is matched against
 * FOUNDING_MEMBERS above; unknown codes are silently ignored.
 */
export function getFoundingMember() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(FOUNDING_MEMBER_KEY);
    if (!raw) return null;
    const rec = JSON.parse(raw);
    if (!rec?.email || !rec?.badge) return null;
    return rec;
  } catch (_e) {
    return null;
  }
}

export function isFoundingMember() {
  return getFoundingMember() !== null;
}

/** Activate a founding-member record from a URL code. Returns the member
    object on success, null if the code is not on the allowlist. */
export function activateFoundingMember(code) {
  if (typeof window === "undefined" || !code) return null;
  const member = FOUNDING_MEMBERS.find((m) => m.code === code);
  if (!member) return null;
  const rec = {
    email: member.email,
    name: member.name,
    badge: member.badge,
    activated_at: Date.now(),
  };
  window.localStorage.setItem(FOUNDING_MEMBER_KEY, JSON.stringify(rec));
  window.dispatchEvent(new CustomEvent("eviq:subscription-changed"));
  return rec;
}

export function clearFoundingMember() {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(FOUNDING_MEMBER_KEY);
  window.dispatchEvent(new CustomEvent("eviq:subscription-changed"));
}

/** Auto-activate founding membership from the signed-in Tesla account email.
    Called after /api/tesla/status resolves — makes founding access follow
    the member's Tesla sign-in on any device (localStorage-independent). */
export function activateFoundingByEmail(email) {
  if (typeof window === "undefined" || !email) return null;
  const member = FOUNDING_MEMBERS.find(
    (m) => m.email.toLowerCase() === String(email).trim().toLowerCase()
  );
  if (!member) return null;
  const existing = getFoundingMember();
  if (existing) return existing;
  const rec = {
    email: member.email,
    name: member.name,
    badge: member.badge,
    activated_at: Date.now(),
  };
  window.localStorage.setItem(FOUNDING_MEMBER_KEY, JSON.stringify(rec));
  window.dispatchEvent(new CustomEvent("eviq:subscription-changed"));
  return rec;
}

/**
 * Open the Welcome / Subscribe gate from anywhere in the app.
 * Any CTA can call this — the actual modal lives in <WelcomeGate />
 * and listens for the `eviq:open-welcome-gate` event.
 */
export function openSubscribeGate(source = "unspecified") {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(OPEN_GATE_EVENT, { detail: { source } })
  );
}

export const SUBSCRIBE_GATE_EVENT = OPEN_GATE_EVENT;

/**
 * React hook: subscribe to subscription-status changes so components
 * re-render when the visitor upgrades or their preview expires.
 */
import { useEffect, useState } from "react";

export function useSubscription() {
  const compute = () => ({
    status: getSubscriptionStatus(),
    tier: getEffectiveTier(),
    paid: hasPaidSubscription(),
    isSubscribed: isSubscribed() || getEffectiveTier() === "preview",
    hasPremium: hasPremiumFeatures(),
    preview: getPreviewState(),
    founding: getFoundingMember(),
  });
  const [state, setState] = useState(compute);

  useEffect(() => {
    const onChange = () => setState(compute());
    window.addEventListener("eviq:subscription-changed", onChange);
    window.addEventListener("storage", onChange); // cross-tab sync
    // Re-check every minute so the countdown UI stays fresh without needing
    // an explicit event when time simply passes.
    const t = setInterval(onChange, 60 * 1000);
    return () => {
      window.removeEventListener("eviq:subscription-changed", onChange);
      window.removeEventListener("storage", onChange);
      clearInterval(t);
    };
  }, []);

  return state;
}
